import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { prefersReducedMotion } from './reduced-motion';

type TAnimationKind = 'dialog' | 'popover';

type TRunOnTransitionEndArgs = {
	element: HTMLElement;
	safetyNetMs: number;
	onSettled: () => void;
};

/**
 * Using the recommended motion timings for safety net.
 *
 * Not setting these timings on the preset config because they're defined in the CSS,
 * so configuring it in the preset doesn't achieve anything except introduce a potential
 * source of drift.
 *
 * TODO: figure out a way to not need to redeclare these in the future,
 * and keep CSS and JS timings in sync always.
 */
const safetyNetMsMap: Record<TAnimationKind, { enter: number; exit: number }> = {
	dialog: {
		enter: 250, // Corresponds to `motion.duration.long`
		exit: 200, // Corresponds to `motion.duration.medium`
	},
	popover: {
		enter: 150, // Corresponds to `motion.duration.short`
		exit: 100, // Corresponds to `motion.duration.xshort`
	},
};

/**
 * Fires `onSettled` once when `transitionend` fires on `element`, or after
 * `safetyNetMs` if the event is swallowed (mid-transition unmount, CSS
 * mismatch, browser quirks). Returns a cleanup that cancels both paths.
 */
function runOnTransitionEndOrTimeout({
	element,
	safetyNetMs,
	onSettled,
}: TRunOnTransitionEndArgs): () => void {
	const state = { isSettled: false };

	function teardown() {
		state.isSettled = true;
		clearTimeout(safetyNetId);
		unbind();
	}

	function settle() {
		if (state.isSettled) {
			return;
		}
		teardown();
		onSettled();
	}

	const unbind = bind(element, {
		type: 'transitionend',
		listener: settle,
		options: { once: true },
	});
	const safetyNetId = setTimeout(settle, safetyNetMs);

	return teardown;
}

type TUseAnimatedVisibilityArgs = {
	/**
	 * Whether the element is logically open.
	 */
	isOpen: boolean;
	/**
	 * Animation kind used to select default safety-net timings.
	 */
	animationKind: TAnimationKind;
	/**
	 * Animation config for entry/exit transitions.
	 * Pass `false` or `undefined` to disable animation.
	 */
	shouldAnimate: boolean;
	/**
	 * Ref to the DOM element that plays the entry/exit transitions.
	 * Used to listen for `transitionend`.
	 */
	elementRef: RefObject<HTMLElement | null>;
	/**
	 * Called after the entry animation completes (or immediately on open
	 * when there is no animation or reduced motion is active).
	 * This includes initial mount with `isOpen=true`.
	 */
	onEnterFinish?: () => void;
	/**
	 * Called after the exit animation completes (or immediately on close
	 * when there is no animation or reduced motion is active).
	 */
	onExitFinish?: () => void;
};

type TUseAnimatedVisibilityResult = {
	/**
	 * Current visibility phase. See `TPhase` for the full lifecycle.
	 *
	 * `phase !== 'closed'` means the host element is mounted and on
	 * screen, including during animated entry, animated exit, and the
	 * non-animated close handshake. Use this when you need "the user
	 * can still see / interact with the element" semantics (e.g. focus
	 * trapping, listeners that must outlive the exit).
	 */
	phase: TPhase;
	/**
	 * Notifies the visibility lifecycle after the primitive has finished its
	 * own native toggle handling. Closing toggles make native dismissal
	 * authoritative while preserving primitive-specific handler ordering.
	 */
	onToggle: (event: ToggleEvent) => void;
};

/**
 * Visibility lifecycle phase shared between `useAnimatedVisibility`,
 * `useFocusWrap`, `useInitialFocus`, and the `Popover` / `Dialog`
 * components.
 *
 * - `closed`: host element is unmounted. No DOM presence.
 * - `entering`: host element is mounted and the CSS entry transition
 *   is playing. **Only emitted when animation is enabled.** With
 *   animation disabled the lifecycle goes directly `closed → open`.
 * - `open`: host element is mounted and settled. No transition is
 *   playing.
 * - `exiting`: `isOpen` has flipped to `false` but the host element
 *   is still mounted. Emitted while the CSS exit transition is playing
 *   (animation enabled) OR while we wait for the browser's
 *   `toggle`/`close` event to fire on the still-attached element so
 *   focus restoration can run (animation disabled).
 *
 * State machine:
 * ```
 *   animation ON:   closed → entering → open → exiting → closed
 *   animation OFF:  closed →            open → exiting → closed
 *   interrupts:     entering → exiting (close mid-entry)
 *                   exiting  → entering (reopen mid-exit, animated)
 *                   exiting  → open     (reopen mid-handshake, non-animated)
 * ```
 *
 * `phase !== 'closed'` is the canonical "host is currently in the DOM
 * and on screen" predicate.
 */
export type TPhase = 'closed' | 'entering' | 'open' | 'exiting';

/**
 * Manages the children mount/unmount lifecycle around CSS exit transitions.
 *
 * Used by both `Popover` and `Dialog` to share the same animation lifecycle
 * logic. Those components own their own show/hide mechanisms (showPopover /
 * hidePopover vs showModal / close) and event handling - this hook only
 * manages the relationship between `isOpen` and when children are rendered.
 *
 * ## Problem
 *
 * We want to delay the unmount of children until a CSS exit transition has
 * finished playing. If we unmount children the moment `isOpen` becomes
 * `false`, the exit animation is never visible - the content just disappears.
 *
 * ## How it works
 *
 * The lifecycle is modeled as a `phase` discriminated union (`closed`,
 * `entering`, `open`, `exiting`). The host element is rendered while
 * `phase !== 'closed'`, so the `exiting` phase keeps the host mounted
 * while the CSS exit transition plays (or, with animation disabled,
 * while we wait for the browser's closing `toggle` event to fire on the
 * still-attached element):
 *
 * ```
 * isOpen:  true ──────────────── false
 * phase:   open ──────────────── exiting ─── (exit settle) ─── closed
 * ```
 *
 * Every effect dispatches on the single `phase` value instead of a
 * cross-product of booleans.
 */
export function useAnimatedVisibility({
	isOpen,
	animationKind,
	shouldAnimate,
	elementRef,
	onEnterFinish,
	onExitFinish,
}: TUseAnimatedVisibilityArgs): TUseAnimatedVisibilityResult {
	const willAnimate = shouldAnimate && !prefersReducedMotion();
	const isNativeClosedRef = useRef(false);

	// Promote `closed → entering`/`closed → open` synchronously during
	// render so the host element mounts in the same commit as the open
	// intent. All other transitions are driven by phase-specific effects.
	const [phase, setPhase] = useState<TPhase>(() => {
		if (!isOpen) {
			return 'closed';
		}
		return willAnimate ? 'entering' : 'open';
	});

	// Rearm only after controlled state and the native lifecycle have both
	// reached closed. Resetting as soon as `isOpen` becomes false can erase a
	// closing toggle that was handled in the same React batch.
	if (!isOpen && phase === 'closed') {
		isNativeClosedRef.current = false;
	}

	if (isOpen && phase === 'closed' && !isNativeClosedRef.current) {
		setPhase(willAnimate ? 'entering' : 'open');
	}
	if (!isOpen && (phase === 'open' || phase === 'entering')) {
		// Close intent. Move to `exiting` to gate the unmount on either
		// the exit transition (animated) or the closing toggle event
		// (non-animated). Includes interrupted entry (`entering → exiting`).
		setPhase('exiting');
	}
	if (isOpen && phase === 'exiting' && !isNativeClosedRef.current) {
		// Reopen mid-exit. With animation, jump back to `entering` and let
		// the entry transition restart from current visual state. Without
		// animation, jump straight to `open` (the handshake we were waiting
		// for is no longer relevant; the consumer wants the host visible
		// again synchronously).
		setPhase(willAnimate ? 'entering' : 'open');
	}

	// Keep stable refs to the latest callbacks so effects and timeout closures
	// always call the current callback without needing them in dependency arrays.
	const onEnterFinishRef = useRef(onEnterFinish);
	onEnterFinishRef.current = onEnterFinish;

	const onExitFinishRef = useRef(onExitFinish);
	onExitFinishRef.current = onExitFinish;

	// Animated entry: wait for `transitionend` (or a safety-net timeout) and
	// move to `open`. The fallback guarantees settle on swallowed events
	// (zero-duration, browser quirk, mid-transition unmount).
	useEffect(() => {
		if (phase !== 'entering') {
			return;
		}

		const element = elementRef.current;
		if (!element) {
			return;
		}

		return runOnTransitionEndOrTimeout({
			element,
			safetyNetMs: safetyNetMsMap[animationKind].enter + 50,
			onSettled: () => {
				// Fire the consumer callback before transitioning phase so any
				// state the consumer reads (refs, DOM nodes) is still attached
				// in the same synchronous tick, regardless of React batching
				// semantics.
				onEnterFinishRef.current?.();
				setPhase((current) => (current === 'entering' ? 'open' : current));
			},
		});
	}, [phase, animationKind, elementRef]);

	// Non-animated entry callback. The lifecycle goes `closed → open`
	// directly with no `entering` phase; fire `onEnterFinish` on that jump.
	// A previous-phase ref avoids double-firing on the animated path.
	const prevPhaseForEntryRef = useRef<TPhase>('closed');
	useEffect(() => {
		const prevPhase = prevPhaseForEntryRef.current;
		prevPhaseForEntryRef.current = phase;

		// Animated entry settle goes through `entering` and already fired
		// onEnterFinish above; only handle the non-animated direct jumps.
		if (prevPhase !== 'open' && phase === 'open' && prevPhase !== 'entering') {
			onEnterFinishRef.current?.();
		}
	}, [phase]);

	// Animated exit: wait for `transitionend` (or a safety-net timeout),
	// fire `onExitFinish`, then move to `closed` to unmount the host.
	useEffect(() => {
		if (phase !== 'exiting' || !willAnimate) {
			return;
		}

		const element = elementRef.current;
		if (!element) {
			return;
		}

		return runOnTransitionEndOrTimeout({
			element,
			safetyNetMs: safetyNetMsMap[animationKind].exit + 50,
			onSettled: () => {
				// Fire the consumer callback before transitioning phase to
				// `closed`. The phase change drives the host element unmount,
				// and consumers expect to be able to read the element (or a
				// forwarded ref to it) inside `onExitFinish`. Doing this in
				// the safe order avoids relying on React's automatic batching
				// to keep the element attached across the callback.
				onExitFinishRef.current?.();
				setPhase((current) => (current === 'exiting' ? 'closed' : current));
			},
		});
	}, [phase, willAnimate, animationKind, elementRef]);

	// Native dismissal is authoritative. Its toggle can arrive while controlled
	// state still says open, so remember that native state and do not
	// reopen until `isOpen` has first moved to false.
	useLayoutEffect(() => {
		if (phase === 'exiting' && !willAnimate && isNativeClosedRef.current) {
			setPhase('closed');
		}
	}, [phase, willAnimate]);

	const onToggle = useCallback(
		(event: ToggleEvent) => {
			if (event.newState === 'open') {
				isNativeClosedRef.current = false;
				return;
			}
			if (event.newState !== 'closed') {
				return;
			}

			isNativeClosedRef.current = true;
			setPhase((current) => {
				if (current === 'open' || current === 'entering') {
					return 'exiting';
				}
				if (current === 'exiting' && !willAnimate) {
					return 'closed';
				}
				return current;
			});
		},
		[willAnimate],
	);

	// Non-animated exit callback. No `transitionend` to wait for, so fire
	// `onExitFinish` on the transition into `exiting` (the unmount itself
	// is gated by the closing toggle handshake above).
	const prevPhaseForExitRef = useRef<TPhase>('closed');
	useEffect(() => {
		const prevPhase = prevPhaseForExitRef.current;
		prevPhaseForExitRef.current = phase;

		// Only fire on the non-animated transition into `exiting`. The
		// animated path's `exiting → closed` settle already fires
		// `onExitFinish` from `runOnTransitionEndOrTimeout`.
		if (prevPhase !== 'exiting' && phase === 'exiting' && !willAnimate) {
			onExitFinishRef.current?.();
		}
	}, [phase, willAnimate]);

	return { phase, onToggle };
}
